import { ChangeEventHandler, useContext, useState } from "react";
import { useCandidateNotesQuery, useSaveNoteMutation } from "../../../../App/Queries/NoteQueries";
import { useNotification } from "../../../../App/State/Hooks/notificationHooks";
import { CandidateContext } from "../../../CandidateProfile";
import { useCurrentUser } from "../../../../App/Queries/UserQueries";

export function useNotesState() {
  const { candidate } = useContext(CandidateContext);
  //get data from redux
  const user = useCurrentUser();

  const { displaySuccess, displayError } = useNotification();

  //local state
  const [newNote, setNewNote] = useState<string>("");
  const [isNoteValid, setIsNoteValid] = useState<boolean>(true);
  const [notesInEditMode, setNotesInEditMode] = useState<string[]>([] as string[]);

  const saveNoteMutation = useSaveNoteMutation();
  const { isLoading: saveNoteLoading } = saveNoteMutation;

  const { data: notes, isFetched: notesFetched } = useCandidateNotesQuery(candidate.id);

  /**
   * @description keeps the progress of a new note as the user changes it
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  const handleNoteChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    setNewNote((oldValue) => event.target.value);
    setIsNoteValid(event.target.value.length <= 1500 ? true : false);
  };

  /**
   * @description saves a new note to the Candidates record
   *
   * @returns void
   */
  const handleNoteSave = (): void => {
    if (newNote.length <= 0 || !candidate) {
      return;
    }
    if (newNote.length > 1500) {
      displayError("Notes must be less than 1500 characters. Please edit your note and try again.");
      return;
    }
    saveNoteMutation.mutate(
      {
        candidateId: candidate.id,
        note: newNote,
        userId: user.id,
      },
      {
        onError: () => displayError("There was an error saving your note. Please try again!"),
        onSuccess: () => {
          displaySuccess("Your note was successfully saved!");
          setNewNote((prev) => "");
        },
      }
    );
  };

  return {
    effects: {},
    getters: {
      isNoteValid,
      newNote,
      notes: notes ?? [],
      notesFetched,
      notesInEditMode,
      user,
      saveNoteLoading,
    },
    handlers: {
      handleNoteChange,
      handleNoteSave,
    },
    setters: {
      setNewNote,
      setNotesInEditMode,
    },
  };
}
